import { useState } from "react";
import { TariffWithPricesViewType } from "../../../store/Catalogs/types";
import { Input } from "../../../components/ui/input";
import { X } from "lucide-react";

interface TariffVariantDialogProps {
	tariff: TariffWithPricesViewType;
	isOpen: boolean;
	onClose: () => void;
	onCreateVariant: (codeE: string) => Promise<void>;
}

export const TariffVariantDialog: React.FC<TariffVariantDialogProps> = ({
	tariff,
	isOpen,
	onClose,
	onCreateVariant,
}) => {
	const [codeE, setCodeE] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		
		// Validate input
		if (!codeE.trim()) {
			setError("Bitte geben Sie einen Eingabecode ein");
			return;
		}

		setIsLoading(true);
		setError("");

		try {
			await onCreateVariant(codeE.trim());
			// Close dialog and reset form on success
			setCodeE("");
			onClose();
		} catch (err) {
			// Error handling is done in the parent component
			// Just stop loading here
		} finally {
			setIsLoading(false);
		}
	};

	const handleClose = () => {
		if (!isLoading) {
			setCodeE("");
			setError("");
			onClose();
		}
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center">
			{/* Backdrop */}
			<div 
				className="absolute inset-0 bg-black bg-opacity-50"
				onClick={handleClose}
			/>
			
			{/* Dialog */}
			<div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
				{/* Header */}
				<div className="flex items-center justify-between p-4 border-b">
					<h2 className="text-lg font-semibold">Variante erstellen</h2>
					<button
						onClick={handleClose}
						disabled={isLoading}
						className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
					>
						<X className="h-5 w-5" />
					</button>
				</div>

				{/* Content */}
				<form onSubmit={handleSubmit}>
					<div className="p-4 space-y-4">
						{/* Original tariff info */}
						<div className="bg-gray-50 p-3 rounded">
							<div className="text-sm text-gray-600 mb-1">Originalleistung:</div>
							<div className="font-medium">{tariff.code || ''}</div>
							<div className="text-sm text-gray-600 mt-1">
								{tariff.description_de || 'Keine Beschreibung'}
							</div>
						</div>

						{/* New code_e input */}
						<div>
							<label htmlFor="code_e" className="block text-sm font-medium text-gray-700 mb-1">
								Neuer Eingabecode <span className="text-red-500">*</span>
							</label>
							<Input
								id="code_e"
								type="text"
								value={codeE}
								onChange={(e) => {
									setCodeE(e.target.value);
									setError("");
								}}
								placeholder="z.B. 4.0010V"
								disabled={isLoading}
								autoFocus
							/>
							{error && (
								<p className="mt-1 text-sm text-red-600">{error}</p>
							)}
							<p className="mt-1 text-xs text-gray-500">
								Der Eingabecode muss eindeutig sein und wird für die schnelle Eingabe verwendet.
							</p>
						</div>
					</div>

					{/* Footer */}
					<div className="flex justify-end gap-2 p-4 border-t">
						<button
							type="button"
							onClick={handleClose}
							disabled={isLoading}
							className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
						>
							Abbrechen
						</button>
						<button
							type="submit"
							disabled={isLoading || !codeE.trim()}
							className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{isLoading ? "Erstelle..." : "Variante erstellen"}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};